import { type ReactNode } from 'react'

import { Banner, Layout, Text } from '@loadsmart/miranda-react'

import { ErrorBoundary } from 'core/ui/components/error-boundary/ErrorBoundary'
import useWindowWidth from 'core/ui/hooks/useWindowWidth'

import { intSizes } from 'legacy/styles/screen'
import user from 'legacy/utils/user'

import { analytics } from '../../core/utils/analytics'
import { type AgnosticShipment } from '../domain/AgnosticShipment'
import {
  formatAgnosticIdentifiers,
  formatSectionCountSubtitle,
  formatStatusTagLabel,
  formatStatusTagVariant,
} from '../presenters/agnosticShipmentPresenter'
import { ShipmentCard } from './ShipmentCard'

function ShipmentListError() {
  return (
    <Banner variant="danger">
      <Banner.Description>
        Unable to load shipments at the moment. Please try again later.
      </Banner.Description>
    </Banner>
  )
}

export type AgnosticShipperListSectionProps = {
  count: number
  shipments?: Array<AgnosticShipment>
  title: string
  actions?: ReactNode
  onActionClick: (shipmentURL: string) => void
}

export function AgnosticShipperListSection({
  count,
  shipments,
  actions,
  onActionClick,
}: AgnosticShipperListSectionProps) {
  const windowWidth = useWindowWidth()
  const compact = windowWidth <= intSizes.md

  if (!shipments || shipments.length === 0) return null

  /**
   * If you're testing locally you'll be redirected to https://carrier.staging.loadsmart.com. Replace the argument
   * `shipment.shipmentURL` to `shipment.shipmentURL.replace(/.*\.com/g, 'http://localhost:8080')` to work locally.
   */
  function handleOnCardClick(shipment: AgnosticShipment) {
    analytics.track('Clicked: Shipment Card', {
      shipper: shipment.shipper.companyName,
      status: shipment.status,
      logged: user.isLoggedIn(),
    })

    onActionClick?.(shipment.shipmentURL)
  }

  return (
    <ErrorBoundary fallback={ShipmentListError}>
      <Layout.Stack gap="spacing-4" style={{ width: '100%' }}>
        <Layout.Group align="center" justify="space-between" style={{ width: '100%' }}>
          <Text variant="body-md-bold">{formatSectionCountSubtitle(count)}</Text>
          {actions}
        </Layout.Group>
        {shipments.map(shipment => (
          <ShipmentCard
            key={shipment.uuid}
            shipment={shipment}
            getStatus={() => ({
              variant: formatStatusTagVariant(shipment),
              label: formatStatusTagLabel(shipment),
            })}
            getIdentifiers={() => formatAgnosticIdentifiers(compact, shipment)}
            onCardClick={() => handleOnCardClick(shipment)}
          />
        ))}
      </Layout.Stack>
    </ErrorBoundary>
  )
}
